import React, { useEffect, useState } from 'react';
import { Alert, Card, Col, Container, Row, Table } from 'react-bootstrap';
import { FaBuilding } from 'react-icons/fa';
import { useTable } from 'react-table';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL;

function FactoryBranchAdmin() {
  // Table state
  const [branchAdmins, setBranchAdmins] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Get user data from localStorage
  const userData = JSON.parse(localStorage.getItem("user") || "{}");
  const FactoryCode = userData?.FactoryCode;

  // Fetch branch admins data
  const fetchBranchAdmins = async () => {
    setIsLoading(true);
    try {
      if (!FactoryCode) {
        throw new Error('Factory code is required');
      }

      const response = await fetch(`${BASE_URL}/get-factory-branch-admins-detailed?FactoryCode=${FactoryCode}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setBranchAdmins(result.data);
        setError('');
      } else {
        throw new Error(result.message || 'Failed to fetch branch admins');
      }
    } catch (err) {
      console.error("Failed to fetch branch admins:", err);
      setError('Failed to load branch admins. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Load branch admins on component mount
  useEffect(() => {
    if (FactoryCode) {
      fetchBranchAdmins();
    }
  }, [FactoryCode]);

  // Table columns
  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'firstName',
        Cell: ({ row }) => `${row.original.firstName} ${row.original.lastName}`,
      },
      {
        Header: 'Email',
        accessor: 'emailAddress',
      },
     
      {
        Header: 'Phone Number',
        accessor: 'phoneNumber',
      },
      {
        Header: 'Usage Statistics',
        accessor: 'usage',
        Cell: ({ row }) => (
          <div className="text-center">
            <small className="text-muted d-block">
              <strong>Branches:</strong> {row.original.branchCount || 0}/{row.original.BranchQty || 0}
            </small>
            <small className="text-muted d-block">
              <strong>Departments:</strong> {row.original.departmentCount || 0}/{row.original.DepartmentQty || 0}
            </small>
            <small className="text-muted d-block">
              <strong>Auditors:</strong> {row.original.auditorCount || 0}/{row.original.AuditorUsers || 0}
            </small>
            <small className="text-muted d-block">
              <strong>Dept Users:</strong> {row.original.departmentUserCount || 0}/{row.original.DepartmentUsers || 0}
            </small>
          </div>
        ),
      },
      {
        Header: 'Join Date',
        accessor: 'create_date',
        Cell: ({ value }) => {
          if (!value) return 'N/A';
          return new Date(value).toLocaleDateString();
        }
      }
    ],
    []
  );

  // Create a table instance using react-table
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({
    columns,
    data: branchAdmins,
  });

  return (
    <Container fluid className="py-4">
      {/* Notification alert */}
      <ToastContainer />
      
      <Container fluid className="py-3">
        <Row className="justify-content-center">
          <Col xs={12}>
            <Card className="shadow-sm">
              <Card.Header className="bg-primary text-white">
                <Row className="align-items-center">
                  <Col>
                    <h4 className="mb-0">Factory Branch Administrators</h4>
                    <small className="opacity-75">View branch administrators within your factory network</small>
                  </Col>
                  <Col md="auto">
                    <div className="text-end">
                      <small className="opacity-75">Total Admins: {branchAdmins.length}</small>
                    </div>
                  </Col>
                </Row>
              </Card.Header>
              
              <Card.Body>
                {/* Error message */}
                {error && <Alert variant="danger">{error}</Alert>}
                
                {/* Enhanced Loading indicator */}
                {isLoading && (
                  <div className="text-center py-5">
                    <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-primary fw-bold mb-1">Loading Branch Administrators</p>
                    <p className="text-muted">Please wait while we fetch your admin data...</p>
                  </div>
                )}
                
                {/* React Table (Responsive) - only show when not loading */}
                {!isLoading && (
                  <>
                    {branchAdmins.length === 0 ? (
                      <div className="text-center py-4">
                        <FaBuilding className="text-muted mb-3" size={48} />
                        <p className="text-muted mb-0">No branch administrators found.</p>
                        <p className="text-muted">There are currently no branch administrators in your factory network.</p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <Table hover striped bordered {...getTableProps()} className="align-middle">
                          <thead className="bg-light">
                            {headerGroups.map(headerGroup => (
                              <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                  <th {...column.getHeaderProps()} className="text-center">
                                    {column.render('Header')}
                                  </th>
                                ))}
                              </tr>
                            ))}
                          </thead>
                          <tbody {...getTableBodyProps()}>
                            {rows.map(row => {
                              prepareRow(row);
                              return (
                                <tr {...row.getRowProps()}>
                                  {row.cells.map(cell => {
                                    return (
                                      <td {...cell.getCellProps()} className="text-center">
                                        {cell.render('Cell')}
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
         
            </Card>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        /* Enhanced loading animation */
        .spinner-grow {
          animation-duration: 1.5s;
        }
        
        .text-primary.fw-bold {
          font-size: 1.2rem;
        }
        
        /* Table styles */
        .table th {
          background-color: #f8f9fa !important;
          font-weight: 600;
          border-color: #dee2e6;
        }
        
        .table td {
          border-color: #dee2e6;
          vertical-align: middle;
        }
        
        /* Card styles */
        .card {
          border: none;
          border-radius: 10px;
        }
        
        .card-header {
          border-radius: 10px 10px 0 0 !important;
        }
        
        /* Badge styles */
        .badge {
          font-size: 0.75rem;
          padding: 0.375rem 0.75rem;
        }
        
        /* Responsive table styling */
        @media (max-width: 768px) {
          .table-responsive {
            font-size: 0.875rem;
          }
          
          .card-footer {
            font-size: 0.8rem;
          }
        }
        
        /* Row hover effect */
        .table tbody tr:hover {
          background-color: rgba(0, 123, 255, 0.05);
        }
      `}</style>
    </Container>
  );
}

export default FactoryBranchAdmin;